// src/break-rules.ts
var STATUTORY_THRESHOLDS = [
  { minHours: 9, breakMinutes: 45 },
  { minHours: 6, breakMinutes: 30 }
];
function calculateStatutoryBreak(grossMinutes) {
  const grossHours = grossMinutes / 60;
  for (const threshold of STATUTORY_THRESHOLDS) {
    if (grossHours > threshold.minHours) {
      return threshold.breakMinutes;
    }
  }
  return 0;
}
function calculateBreak(grossMinutes, config, entry) {
  switch (config.type) {
    case "STATUTORY":
      return calculateStatutoryBreak(grossMinutes);
    case "FIXED":
      return Math.min(config.fixedMinutes, grossMinutes);
    case "MANUAL":
      return entry?.breakMinutes ?? 0;
    default: {
      const _exhaustive = config;
      return _exhaustive;
    }
  }
}
function validateBreak(grossMinutes, actualBreakMinutes) {
  const requiredBreak = calculateStatutoryBreak(grossMinutes);
  if (actualBreakMinutes >= requiredBreak) {
    return { valid: true };
  }
  return {
    valid: false,
    requiredMinutes: requiredBreak,
    actualMinutes: actualBreakMinutes,
    shortfallMinutes: requiredBreak - actualBreakMinutes
  };
}
var BREAK_THRESHOLDS = STATUTORY_THRESHOLDS;

// src/duration.ts
function getMinutesBetween(startTime, endTime) {
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  if (isNaN(start) || isNaN(end)) {
    throw new Error(`Invalid timestamps: start="${startTime}", end="${endTime}"`);
  }
  const diffMs = end - start;
  if (diffMs < 0) {
    throw new Error(`End time is before start time: start="${startTime}", end="${endTime}"`);
  }
  return Math.round(diffMs / (1e3 * 60));
}
function calculateGrossDuration(entry) {
  if (entry.endTime === null) {
    return 0;
  }
  return getMinutesBetween(entry.startTime, entry.endTime);
}
function calculateEntryNetDuration(entry, config) {
  const grossMinutes = calculateGrossDuration(entry);
  const breakMinutes = calculateBreak(grossMinutes, config.breakRule, entry);
  return Math.max(0, grossMinutes - breakMinutes);
}
function calculateNetDuration(entries, config) {
  return entries.reduce((total, entry) => {
    return total + calculateEntryNetDuration(entry, config);
  }, 0);
}
function calculateDurationStats(entries, config) {
  let totalGrossMinutes = 0;
  let totalBreakMinutes = 0;
  let totalNetMinutes = 0;
  const entryStats = entries.map((entry) => {
    const grossMinutes = calculateGrossDuration(entry);
    const breakMinutes = calculateBreak(grossMinutes, config.breakRule, entry);
    const netMinutes = Math.max(0, grossMinutes - breakMinutes);
    totalGrossMinutes += grossMinutes;
    totalBreakMinutes += breakMinutes;
    totalNetMinutes += netMinutes;
    return {
      entryId: entry.id,
      employeeId: entry.employeeId,
      grossMinutes,
      breakMinutes,
      netMinutes
    };
  });
  return {
    totalGrossMinutes,
    totalBreakMinutes,
    totalNetMinutes,
    entryCount: entries.length,
    entries: entryStats
  };
}
function minutesToDecimalHours(minutes, decimals = 2) {
  const hours = minutes / 60;
  const factor = Math.pow(10, decimals);
  return Math.round(hours * factor) / factor;
}
function formatMinutesAsTime(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}

// src/datev-export.ts
function getAbsenceCode(type) {
  switch (type) {
    case "SICK":
      return "K";
    case "VACATION":
      return "U";
    default:
      return "";
  }
}
function makeAggregationKey(personnelNumber, absenceCode) {
  return `${personnelNumber}|${absenceCode}`;
}
function generateDatevExportRows(timeSheet, entries, config) {
  if (timeSheet.status !== "APPROVED" && timeSheet.status !== "EXPORTED") {
    throw new DatevExportError(
      `TimeSheet ${timeSheet.id} hat Status "${timeSheet.status}", erwartet "APPROVED" oder "EXPORTED"`
    );
  }
  const day = extractDayFromDate(timeSheet.date);
  const aggregated = /* @__PURE__ */ new Map();
  for (const entry of entries) {
    if (entry.type === "BREAK") {
      continue;
    }
    const absenceCode = getAbsenceCode(entry.type);
    const key = makeAggregationKey(entry.personnelNumber, absenceCode);
    const isAbsence = entry.type === "SICK" || entry.type === "VACATION";
    const hours = isAbsence ? 0 : minutesToDecimalHours(calculateEntryNetDuration(entry, config));
    const existing = aggregated.get(key);
    if (existing) {
      existing.value += hours;
    } else {
      aggregated.set(key, {
        personnelNumber: entry.personnelNumber,
        day,
        wageType: config.datevConfig.wageTypeHours,
        absenceCode,
        value: hours
      });
    }
  }
  return Array.from(aggregated.values());
}
function generateMonthlyDatevExport(timeSheets, config) {
  const allRows = [];
  for (const { sheet, entries } of timeSheets) {
    if (sheet.status !== "APPROVED" && sheet.status !== "EXPORTED") {
      continue;
    }
    const rows = generateDatevExportRows(sheet, entries, config);
    allRows.push(...rows);
  }
  return allRows.sort((a, b) => {
    const pnrCompare = a.personnelNumber.localeCompare(b.personnelNumber);
    if (pnrCompare !== 0) return pnrCompare;
    return a.day - b.day;
  });
}
function serializeDatevCsv(rows, options = {}) {
  const {
    includeHeader = true,
    delimiter = ";",
    decimalSeparator = ",",
    lineEnding = "\r\n"
  } = options;
  const lines = [];
  if (includeHeader) {
    lines.push(["Personalnummer", "Tag", "Lohnart", "Ausfallschluessel", "Wert"].join(delimiter));
  }
  for (const row of rows) {
    const valueStr = row.value.toFixed(2).replace(".", decimalSeparator);
    lines.push([
      row.personnelNumber,
      row.day.toString(),
      row.wageType.toString(),
      row.absenceCode,
      valueStr
    ].join(delimiter));
  }
  return lines.join(lineEnding);
}
function validateExportReadiness(timeSheets, expectedMonth) {
  const issues = [];
  for (const sheet of timeSheets) {
    const sheetMonth = sheet.date.substring(0, 7);
    if (sheetMonth !== expectedMonth) {
      issues.push({
        timeSheetId: sheet.id,
        type: "WRONG_MONTH",
        message: `TimeSheet Datum ${sheet.date} geh\xF6rt nicht zum Monat ${expectedMonth}`
      });
      continue;
    }
    if (sheet.status !== "APPROVED" && sheet.status !== "EXPORTED") {
      issues.push({
        timeSheetId: sheet.id,
        type: "NOT_APPROVED",
        message: `TimeSheet ${sheet.date} hat Status "${sheet.status}", erwartet "APPROVED"`
      });
    }
  }
  return {
    valid: issues.length === 0,
    issues,
    approvedCount: timeSheets.filter(
      (s) => s.status === "APPROVED" || s.status === "EXPORTED"
    ).length,
    totalCount: timeSheets.length
  };
}
function extractDayFromDate(isoDate) {
  const day = parseInt(isoDate.substring(8, 10), 10);
  if (isNaN(day) || day < 1 || day > 31) {
    throw new DatevExportError(`Ungueltiges Datum: ${isoDate}`);
  }
  return day;
}
var DatevExportError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "DatevExportError";
  }
};

// src/breakpoints.ts
var BREAKPOINTS = {
  /** Mindestbreite fuer Split-View mit Panel (Desktop) */
  PANEL_MIN_WIDTH: 1200,
  /** Sidebar-Breakpoint (Mobile vs Desktop) */
  SIDEBAR_MIN_WIDTH: 768
};
function isMinWidth(minWidth) {
  if (typeof window === "undefined") return false;
  return window.matchMedia(`(min-width: ${minWidth}px)`).matches;
}
function isPanelLayout() {
  return isMinWidth(BREAKPOINTS.PANEL_MIN_WIDTH);
}
export {
  BREAKPOINTS,
  BREAK_THRESHOLDS,
  DatevExportError,
  calculateBreak,
  calculateDurationStats,
  calculateEntryNetDuration,
  calculateGrossDuration,
  calculateNetDuration,
  calculateStatutoryBreak,
  formatMinutesAsTime,
  generateDatevExportRows,
  generateMonthlyDatevExport,
  getMinutesBetween,
  isMinWidth,
  isPanelLayout,
  minutesToDecimalHours,
  serializeDatevCsv,
  validateBreak,
  validateExportReadiness
};
//# sourceMappingURL=index.js.map